import { BREAKPOINTS } from '@/shared/constants/breakpoints';
import { useTrans } from '@/shared/hooks/useTranslation';
import { cn } from '@/shared/utils';
import { BreadcrumbSwitcherOptions } from '@/view/shared/Breadcrumbs/BreadcrumbSwitcherOptions';
import type { BreadcrumbSwitcherConfig } from '@/viewmodel/shared/breadcrumbs';
import { ChevronDown, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function BreadcrumbSwitcherPopover({ config }: { config: BreadcrumbSwitcherConfig }) {
  const { t } = useTrans();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { onQueryChange } = config;
  const filtered =
    onQueryChange || !query.trim()
      ? config.options
      : config.options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  const changeQuery = useCallback(
    (next: string) => {
      setQuery(next);
      onQueryChange?.(next);
    },
    [onQueryChange],
  );

  const close = useCallback(() => {
    setOpen(false);
    changeQuery('');
  }, [changeQuery]);

  const handleSelect = (value: string) => {
    if (value !== config.currentValue) {
      config.onSelect(value);
    }
    close();
  };

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth < BREAKPOINTS.sm;
    let left: number;
    if (isMobile) {
      left = 8;
    } else {
      const panelWidth = 280;
      left = rect.left;
      if (left + panelWidth > window.innerWidth - 8) {
        left = window.innerWidth - panelWidth - 8;
      }
      if (left < 8) left = 8;
    }
    setPos({ top: rect.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, close]);

  return (
    <span className="ml-1 inline-flex items-center">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((p) => !p);
          changeQuery('');
        }}
        className={cn(
          'cursor-pointer rounded border p-0.5 transition-colors',
          open
            ? 'border-status-info tone-info'
            : 'tone-info hover:border-status-info hover:bg-status-info/15',
        )}
        aria-label="Switch"
      >
        <ChevronDown
          size={12}
          className={cn('transition-transform duration-150', open && 'rotate-180')}
        />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-9999 w-[calc(100vw-1rem)] sm:w-70 overflow-hidden rounded-lg border border-input bg-card shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="flex items-center gap-2 border-b border-input px-3 py-2.5">
              <Search size={14} className="shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => changeQuery(e.target.value)}
                placeholder={config.filterPlaceholder ?? t('common.filterPlaceholder')}
                className="flex-1 bg-transparent text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>

            <BreadcrumbSwitcherOptions
              config={config}
              options={filtered}
              query={query}
              onSelect={handleSelect}
            />
          </div>,
          document.body,
        )}
    </span>
  );
}
